import { useEffect, ReactNode, useRef } from "react";
import useMeasure from "util/useMeasure";
import { scaleLinear, scaleLog, select } from "d3";
import { gain, linePadding, margin, yScaleBottomPadding } from "./constants";
import { DrawParams, EQTypes } from "./types";
import drawEQLine from "./drawEQLine";
import drawCorrectFreqLine from "./drawCorrectFreqLine";
import drawSafeArea from "./drawSafeArea";
import drawCurrentFreqLine from "./drawCurrentFreqLine";
import drawDraggableCircle from "./drawDraggableCircle";
import drawAxis from "./drawAxis";
import GameBoxWithTitle, { borderWidth } from "ui/atoms/GameBoxWithTitle";
import { refrenceTrackIds } from "../../pages/referenceTrackConfig";

export interface Props {
  measurementKey?: string;
  text: string | ReactNode;
  type: EQTypes;
  q: number;
  freq: number;
  freqRange: {
    min: number;
    max: number;
  };
  onChange: (freq: number) => void;
  safeAreaInOctaves: number;
  correctFreq?: number;
}

export default function Graph(props: Props) {
  const { text, freqRange, q, freq, type, onChange, safeAreaInOctaves } = props;
  const { min: minFreq, max: maxFreq } = freqRange;
  const [{ height, width }, sizeRef] = useMeasure<HTMLDivElement>({
    key: props.measurementKey,
  });
  const svgRef = useRef<SVGSVGElement>(null);
  const contentHeight = height - borderWidth * 2;
  const contentWidth = width - borderWidth * 2;

  useEffect(() => {
    if (!svgRef.current) {
      return;
    }

    const svg = select(svgRef.current);

    const xScale = scaleLog()
      .domain([minFreq, maxFreq])
      .range([margin.left, contentWidth - margin.right]);

    const yScale = scaleLinear()
      .domain([gain.cut, gain.boost])
      .range([contentHeight - linePadding.top - yScaleBottomPadding(type), 0]);

    const params: DrawParams = {
      ...props,
      svg,
      xScale,
      yScale,
      contentHeight,
      contentWidth,
    };

    drawAxis(params);
    drawEQLine(params);
    drawCorrectFreqLine(params);
    drawSafeArea(params);
    drawCurrentFreqLine(params);
    drawDraggableCircle(params);
  }, [
    contentWidth,
    contentHeight,
    maxFreq,
    minFreq,
    freq,
    q,
    type,
    freqRange,
    onChange,
    props.correctFreq,
    safeAreaInOctaves,
    props,
  ]);

  return (
    <GameBoxWithTitle title={text} ref={sizeRef}>
      <svg
        id={refrenceTrackIds.graph}
        ref={svgRef}
        className={"absolute left-0 top-0 -z-10 cursor-pointer"}
        style={{ width: contentWidth, height: contentHeight }}
      />
    </GameBoxWithTitle>
  );
}
